import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Crumbs, Section } from "@/components/luminous/interior";
import { Reveal } from "@/components/luminous/primitives";

type Language = 'es' | 'en';

interface BlogProps {
  language: Language;
}

export const Blog = ({ language }: BlogProps) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const content = {
    es: {
      kicker: "Luis ROC Blog",
      title: "Blog de Marketing Digital e IA",
      subtitle: "Guías prácticas para implementar inteligencia artificial en tu negocio y dominar el mercado estadounidense",
      home: "Inicio",
      crumb: "Blog",
      featuredLabel: "Artículo destacado",
      indexLabel: "Todos los artículos",
      categories: {
        all: "Todos los artículos",
        ai: "Inteligencia Artificial",
        business: "Negocios en USA",
        marketing: "Marketing Digital",
        research: "Investigación de Mercado"
      },
      readMore: "Leer",
      readTime: "min de lectura",
      author: "Luis ROC",
      articles: [
        {
          id: 1,
          title: "Cómo Implementar IA en tu Estrategia de Marketing Digital",
          excerpt: "Descubre las herramientas de IA más efectivas para automatizar y mejorar tus campañas de marketing, desde chatbots hasta análisis predictivo.",
          category: "ai",
          readTime: 8,
          date: "2025-05-15",
          slug: "implementar-ia-marketing-digital",
          tags: ["IA", "Marketing", "Automatización", "ChatGPT"],
          featured: true
        },
        {
          id: 2,
          title: "Guía Completa para Crear Prompts Efectivos de IA",
          excerpt: "Aprende a escribir prompts que generen resultados precisos y útiles para tu negocio, con ejemplos prácticos y técnicas avanzadas.",
          category: "ai",
          readTime: 12,
          date: "2025-04-20",
          slug: "crear-prompts-efectivos-ia",
          tags: ["Prompts", "IA", "Productividad", "Copywriting"],
          featured: false
        },
        {
          id: 3,
          title: "Requerimientos FDA para Vender Productos en Estados Unidos",
          excerpt: "Todo lo que necesitas saber sobre regulaciones FDA, etiquetado, registros y compliance para productos alimentarios, cosméticos y suplementos.",
          category: "business",
          readTime: 15,
          date: "2025-04-10",
          slug: "requerimientos-fda-productos-usa",
          tags: ["FDA", "Regulaciones", "Productos", "Compliance"],
          featured: false
        },
        {
          id: 4,
          title: "Cómo Registrar tu Negocio de Servicios en Estados Unidos",
          excerpt: "Paso a paso para establecer tu empresa de servicios en USA: tipos de entidad, licenses, taxes y mejores prácticas legales.",
          category: "business",
          readTime: 10,
          date: "2025-03-25",
          slug: "registrar-negocio-servicios-usa",
          tags: ["LLC", "Servicios", "Registro", "Impuestos"],
          featured: false
        },
        {
          id: 5,
          title: "Estudios de Mercado con IA: Google y Amazon Research",
          excerpt: "Utiliza herramientas de IA para analizar competencia, identificar oportunidades y validar demanda en Google y Amazon marketplace.",
          category: "research",
          readTime: 14,
          date: "2025-03-15",
          slug: "estudios-mercado-ia-google-amazon",
          tags: ["Investigación", "IA", "Google", "Amazon", "Competencia"],
          featured: false
        },
        {
          id: 6,
          title: "Automatización de Campañas Google Ads con IA",
          excerpt: "Cómo usar Google AI y herramientas externas para optimizar bids, crear anuncios y mejorar el ROI de tus campañas automáticamente.",
          category: "marketing",
          readTime: 11,
          date: "2025-02-28",
          slug: "automatizacion-google-ads-ia",
          tags: ["Google Ads", "IA", "Automatización", "ROI"],
          featured: false
        },
        {
          id: 7,
          title: "SEO Predictivo: Usar IA para Anticipar Tendencias",
          excerpt: "Técnicas avanzadas de IA para predecir tendencias de búsqueda, identificar keywords emergentes y adelantarte a la competencia.",
          category: "marketing",
          readTime: 13,
          date: "2025-02-15",
          slug: "seo-predictivo-ia-tendencias",
          tags: ["SEO", "IA", "Tendencias", "Keywords"],
          featured: false
        },
        {
          id: 8,
          title: "Análisis de Competencia en Amazon con Herramientas IA",
          excerpt: "Estrategias para usar IA en el análisis de productos, precios, reviews y ranking de competidores en Amazon marketplace.",
          category: "research",
          readTime: 9,
          date: "2025-01-30",
          slug: "analisis-competencia-amazon-ia",
          tags: ["Amazon", "Competencia", "IA", "Marketplace"],
          featured: false
        },
        {
          id: 9,
          title: "Contenido Escalable: IA para Blogs y Redes Sociales",
          excerpt: "Crea sistemas de contenido escalable usando IA para generar blogs, posts sociales y newsletters que mantengan tu voz de marca.",
          category: "marketing",
          readTime: 16,
          date: "2025-01-15",
          slug: "contenido-escalable-ia-blogs-redes",
          tags: ["Contenido", "IA", "Blogs", "Redes Sociales"],
          featured: false
        },
        {
          id: 10,
          title: "Compliance y Regulaciones para E-commerce en USA",
          excerpt: "Guía completa sobre privacy policies, terms of service, taxes, shipping regulations y protección del consumidor para e-commerce.",
          category: "business",
          readTime: 18,
          date: "2025-01-05",
          slug: "compliance-regulaciones-ecommerce-usa",
          tags: ["E-commerce", "Compliance", "Regulaciones", "Legal"],
          featured: false
        }
      ]
    },
    en: {
      kicker: "Luis ROC Blog",
      title: "Digital Marketing & AI Blog",
      subtitle: "Practical guides to implement artificial intelligence in your business and master the American market",
      home: "Home",
      crumb: "Blog",
      featuredLabel: "Featured article",
      indexLabel: "All articles",
      categories: {
        all: "All Articles",
        ai: "Artificial Intelligence",
        business: "Business in USA",
        marketing: "Digital Marketing",
        research: "Market Research"
      },
      readMore: "Read",
      readTime: "min read",
      author: "Luis ROC",
      articles: [
        {
          id: 1,
          title: "How to Implement AI in Your Digital Marketing Strategy",
          excerpt: "Discover the most effective AI tools to automate and improve your marketing campaigns, from chatbots to predictive analytics.",
          category: "ai",
          readTime: 8,
          date: "2025-05-15",
          slug: "implement-ai-digital-marketing-strategy",
          tags: ["AI", "Marketing", "Automation", "ChatGPT"],
          featured: true
        },
        {
          id: 2,
          title: "Complete Guide to Creating Effective AI Prompts",
          excerpt: "Learn to write prompts that generate accurate and useful results for your business, with practical examples and advanced techniques.",
          category: "ai",
          readTime: 12,
          date: "2025-04-20",
          slug: "create-effective-ai-prompts-guide",
          tags: ["Prompts", "AI", "Productivity", "Copywriting"],
          featured: false
        },
        {
          id: 3,
          title: "FDA Requirements for Selling Products in the United States",
          excerpt: "Everything you need to know about FDA regulations, labeling, registrations, and compliance for food, cosmetics, and supplements.",
          category: "business",
          readTime: 15,
          date: "2025-04-10",
          slug: "fda-requirements-products-usa",
          tags: ["FDA", "Regulations", "Products", "Compliance"],
          featured: false
        },
        {
          id: 4,
          title: "How to Register Your Service Business in the United States",
          excerpt: "Step-by-step guide to establish your service company in the USA: entity types, licenses, taxes, and legal best practices.",
          category: "business",
          readTime: 10,
          date: "2025-03-25",
          slug: "register-service-business-usa",
          tags: ["LLC", "Services", "Registration", "Taxes"],
          featured: false
        },
        {
          id: 5,
          title: "AI-Powered Market Research: Google and Amazon Analysis",
          excerpt: "Use AI tools to analyze competition, identify opportunities, and validate demand on Google and Amazon marketplace.",
          category: "research",
          readTime: 14,
          date: "2025-03-15",
          slug: "ai-market-research-google-amazon",
          tags: ["Research", "AI", "Google", "Amazon", "Competition"],
          featured: false
        },
        {
          id: 6,
          title: "Google Ads Campaign Automation with AI",
          excerpt: "How to use Google AI and external tools to optimize bids, create ads, and automatically improve your campaign ROI.",
          category: "marketing",
          readTime: 11,
          date: "2025-02-28",
          slug: "google-ads-automation-ai",
          tags: ["Google Ads", "AI", "Automation", "ROI"],
          featured: false
        },
        {
          id: 7,
          title: "Predictive SEO: Using AI to Anticipate Trends",
          excerpt: "Advanced AI techniques to predict search trends, identify emerging keywords, and stay ahead of the competition.",
          category: "marketing",
          readTime: 13,
          date: "2025-02-15",
          slug: "predictive-seo-ai-trends",
          tags: ["SEO", "AI", "Trends", "Keywords"],
          featured: false
        },
        {
          id: 8,
          title: "Amazon Competitor Analysis with AI Tools",
          excerpt: "Strategies to use AI for analyzing competitor products, prices, reviews, and rankings in Amazon marketplace.",
          category: "research",
          readTime: 9,
          date: "2025-01-30",
          slug: "amazon-competitor-analysis-ai",
          tags: ["Amazon", "Competition", "AI", "Marketplace"],
          featured: false
        },
        {
          id: 9,
          title: "Scalable Content: AI for Blogs and Social Media",
          excerpt: "Create scalable content systems using AI to generate blogs, social posts, and newsletters while maintaining your brand voice.",
          category: "marketing",
          readTime: 16,
          date: "2025-01-15",
          slug: "scalable-content-ai-blogs-social",
          tags: ["Content", "AI", "Blogs", "Social Media"],
          featured: false
        },
        {
          id: 10,
          title: "Compliance and Regulations for E-commerce in USA",
          excerpt: "Complete guide on privacy policies, terms of service, taxes, shipping regulations, and consumer protection for e-commerce.",
          category: "business",
          readTime: 18,
          date: "2025-01-05",
          slug: "ecommerce-compliance-regulations-usa",
          tags: ["E-commerce", "Compliance", "Regulations", "Legal"],
          featured: false
        }
      ]
    }
  };

  const t = content[language];

  const filteredArticles = selectedCategory === 'all'
    ? t.articles
    : t.articles.filter(article => article.category === selectedCategory);

  const featuredArticle = selectedCategory === 'all' ? t.articles.find(a => a.featured) : undefined;
  const regularArticles = filteredArticles.filter(a => !featuredArticle || a.id !== featuredArticle.id);
  const blogPath = language === 'es' ? '/blog' : '/en/blog';

  return (
    <div className="min-h-screen bg-[color:var(--v3-mineral)]">
      {/* Structured Data for Articles Collection */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": filteredArticles.map((article, index) => ({
            "@type": "Article",
            "position": index + 1,
            "headline": article.title,
            "description": article.excerpt,
            "author": { "@id": "https://luisroc.com/#luisroc" },
            "datePublished": article.date,
            "url": `${blogPath}/${article.slug}`,
            "publisher": { "@id": "https://luisroc.com/#organization" },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `${blogPath}/${article.slug}`
            },
            "keywords": article.tags.join(", "),
            "articleSection": t.categories[article.category as keyof typeof t.categories],
            "inLanguage": language === 'es' ? 'es-US' : 'en-US'
          }))
        })}
      </script>

      <header className="border-b border-[color:var(--v3-line)] bg-[color:var(--v3-mineral)]">
        <div className="v3-shell pb-10 pt-8 lg:pb-14 lg:pt-12">
          <Crumbs items={[{ label: t.home, to: language === "es" ? "/" : "/en" }, { label: t.crumb }]} />
          <p className="v3-micro mt-7">{t.kicker}</p>
          <h1
            className="v3-display mt-4 max-w-3xl"
            style={{ fontSize: "clamp(1.9rem, 3.4vw, 3rem)" }}
          >
            {t.title}
          </h1>
          <p className="v3-body mt-6 text-[1.05rem]">{t.subtitle}</p>

          <nav
            aria-label={language === 'es' ? 'Filtros de categoría de blog' : 'Blog category filters'}
            className="mt-8 flex flex-wrap gap-x-7 gap-y-2 border-t border-[color:var(--v3-line)] pt-5"
          >
            {Object.entries(t.categories).map(([key, label]) => {
              const isSelected = selectedCategory === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSelectedCategory(key)}
                  aria-pressed={isSelected}
                  className="border-b-2 pb-1 text-[0.85rem] font-medium transition-colors"
                  style={{
                    color: isSelected ? "var(--v3-blue)" : "var(--v3-muted)",
                    borderColor: isSelected ? "var(--v3-blue)" : "transparent",
                    transitionDuration: "var(--v3-t-control)",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Artículo destacado: composición amplia, sin cuadrícula */}
      {featuredArticle ? (
        <Section tone="white" label={t.featuredLabel}>
          <Reveal>
            <Link
              to={`${blogPath}/${featuredArticle.slug}`}
              className="group block border-t-2 border-[color:var(--v3-blue)] pt-7"
            >
              <div className="grid gap-6 lg:grid-cols-12 lg:gap-14">
                <div className="lg:col-span-8">
                  <p className="v3-micro">
                    {t.categories[featuredArticle.category as keyof typeof t.categories]}
                    <span className="mx-2" aria-hidden>·</span>
                    {featuredArticle.readTime} {t.readTime}
                  </p>
                  <h2
                    className="v3-display mt-4 transition-colors group-hover:text-[color:var(--v3-blue)]"
                    style={{ fontSize: "clamp(1.6rem, 2.8vw, 2.4rem)" }}
                  >
                    {featuredArticle.title}
                  </h2>
                </div>
                <div className="lg:col-span-4 lg:pt-1">
                  <p className="v3-body">{featuredArticle.excerpt}</p>
                  <span className="v3-inline-link mt-5">
                    {t.readMore}
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </div>
              </div>
            </Link>
          </Reveal>
        </Section>
      ) : null}

      {/* Índice editorial secundario: lista, no cuadrícula de cajas idénticas */}
      <Section label={t.indexLabel}>
        <ul>
          {regularArticles.map((article) => (
            <li key={article.id} className="border-t border-[color:var(--v3-line)] first:border-t-0">
              <Link to={`${blogPath}/${article.slug}`} className="group block py-6">
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                  <p className="v3-micro">
                    {t.categories[article.category as keyof typeof t.categories]}
                    <span className="mx-2" aria-hidden>·</span>
                    {new Date(`${article.date}T00:00:00Z`).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })}
                    <span className="mx-2" aria-hidden>·</span>
                    {article.readTime} {t.readTime}
                  </p>
                </div>
                <h3 className="mt-2 text-[1.1rem] font-semibold leading-snug text-[color:var(--v3-ink)] transition-colors group-hover:text-[color:var(--v3-blue)]">
                  {article.title}
                </h3>
                <p className="v3-note mt-2 max-w-2xl">{article.excerpt}</p>
              </Link>
            </li>
          ))}
        </ul>
      </Section>
    </div>
  );
};
